import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft, Mail, Image, Zap, Lock, BarChart3, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { PASSWORD_REGEX, PASSWORD_RULE_MESSAGE } from '@/lib/validation';

const API_BASE_URL = `${BASE}/api`;

export const FindAccount: React.FC = () => {
  // 아이디 찾기 상태
  const [findIdForm, setFindIdForm] = useState({
    name: '',
    email: '',
  });
  const [foundUserId, setFoundUserId] = useState<string | null>(null);

  // 비밀번호 찾기 상태
  const [findPwForm, setFindPwForm] = useState({
    userId: '',
    email: '',
  });
  const [resetLinkSent, setResetLinkSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 아이디 찾기
  const handleFindId = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFoundUserId(null);
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/find-id`, {
        name: findIdForm.name,
        email: findIdForm.email,
      });

      if (response.data.success) {
        setFoundUserId(response.data.data.userId);
      } else {
        setError(response.data.message || '일치하는 계정을 찾을 수 없습니다.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          '아이디를 찾을 수 없습니다. 입력하신 정보를 확인해주세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  // 비밀번호 찾기 (재설정 링크 발송)
  const handleFindPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResetLinkSent(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset-password-request`, {
        userId: findPwForm.userId,
        email: findPwForm.email,
      });

      if (response.data.success) {
        setResetLinkSent(true);
      } else {
        setError(response.data.message || '비밀번호 재설정 링크 발송에 실패했습니다.');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          '비밀번호 재설정 링크 발송에 실패했습니다. 입력하신 정보를 확인해주세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* 좌측: 서비스 소개 */}
        <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10 max-w-md">
            <div className="mb-8 flex items-center justify-center">
              <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
              </div>
            </div>

            <Link to="/" className="block">
              <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                ALT Generation
              </h1>
            </Link>
            <p className="text-xl text-blue-100 text-center mb-12">
              AI 기반 이미지 ALT 텍스트 자동 생성 시스템
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                  <p className="text-blue-100">
                    AI 기술을 이용한 초고속 ALT 텍스트 생성
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                  <p className="text-blue-100">
                    엔터프라이즈급 보안으로 데이터 보호
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                  <p className="text-blue-100">
                    생성된 ALT 텍스트의 품질 및 사용 현황 분석
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white border-opacity-20">
              <p className="text-blue-100 text-center text-sm">
                웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
              </p>
            </div>
          </div>
        </div>

        {/* 우측: 계정 찾기 폼 */}
        <div className="flex flex-col justify-center items-center p-6 md:p-12">
          <div className="w-full max-w-md mb-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              홈으로
            </Link>
          </div>
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4 lg:hidden">
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-12 h-12 rounded-full" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold">계정 찾기</CardTitle>
              <CardDescription className="text-base mt-2">
                아이디 또는 비밀번호를 찾으실 수 있습니다
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="ml-4 text-sm hover:underline">
                      닫기
                    </button>
                  </AlertDescription>
                </Alert>
              )}

              <Tabs defaultValue="find-id" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="find-id">아이디 찾기</TabsTrigger>
                  <TabsTrigger value="find-password">비밀번호 찾기</TabsTrigger>
                </TabsList>

                {/* 아이디 찾기 */}
                <TabsContent value="find-id">
                  {foundUserId ? (
                    <div className="space-y-4">
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          <p className="font-semibold mb-2">아이디를 찾았습니다!</p>
                          <div className="p-3 bg-white rounded border border-green-300">
                            <p className="text-sm text-gray-600 mb-1">회원님의 아이디:</p>
                            <p className="text-xl font-bold text-gray-900">{foundUserId}</p>
                          </div>
                        </AlertDescription>
                      </Alert>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setFoundUserId(null);
                            setFindIdForm({ name: '', email: '' });
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          다시 찾기
                        </Button>
                        <Link to="/login" className="flex-1">
                          <Button className="w-full">로그인하기</Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleFindId} className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          이름 <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          value={findIdForm.name}
                          onChange={(e) => setFindIdForm({ ...findIdForm, name: e.target.value })}
                          placeholder="가입 시 입력한 이름"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          이메일 <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          value={findIdForm.email}
                          onChange={(e) => setFindIdForm({ ...findIdForm, email: e.target.value })}
                          placeholder="가입 시 입력한 이메일"
                          required
                          disabled={loading}
                        />
                      </div>

                      <Button type="submit" disabled={loading} className="w-full">
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            찾는 중...
                          </>
                        ) : (
                          '아이디 찾기'
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        가입 시 입력한 이름과 이메일을 정확히 입력해주세요.
                      </p>
                    </form>
                  )}
                </TabsContent>

                {/* 비밀번호 찾기 */}
                <TabsContent value="find-password">
                  {resetLinkSent ? (
                    <div className="space-y-4">
                      <Alert className="bg-blue-50 border-blue-200">
                        <Mail className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800">
                          <p className="font-semibold mb-2">요청이 처리되었습니다.</p>
                          <p className="text-sm">
                            입력하신 아이디와 이메일이 일치하는 계정이 있다면,{' '}
                            <strong>{findPwForm.email}</strong>로 비밀번호 재설정 링크가
                            발송됩니다.
                          </p>
                          <p className="text-sm mt-2">
                            이메일을 확인하시고 링크를 클릭하여 비밀번호를 재설정해주세요.
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            ※ 링크는 1시간 동안 유효합니다.
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            이메일이 도착하지 않으면 입력하신 아이디 또는 이메일 주소를 다시 확인해주세요.
                          </p>
                        </AlertDescription>
                      </Alert>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setResetLinkSent(false);
                            setFindPwForm({ userId: '', email: '' });
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          다시 요청
                        </Button>
                        <Link to="/login" className="flex-1">
                          <Button className="w-full">로그인으로</Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleFindPassword} className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          아이디 <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          value={findPwForm.userId}
                          onChange={(e) => setFindPwForm({ ...findPwForm, userId: e.target.value })}
                          placeholder="가입한 아이디"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          이메일 <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          value={findPwForm.email}
                          onChange={(e) => setFindPwForm({ ...findPwForm, email: e.target.value })}
                          placeholder="가입 시 입력한 이메일"
                          required
                          disabled={loading}
                        />
                      </div>

                      <Button type="submit" disabled={loading} className="w-full">
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            발송 중...
                          </>
                        ) : (
                          '재설정 링크 받기'
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        입력하신 이메일로 비밀번호 재설정 링크를 발송해드립니다.
                      </p>
                    </form>
                  )}
                </TabsContent>
              </Tabs>

              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    로그인으로 돌아가기
                  </Button>
                </Link>
                <div className="text-center text-sm">
                  <span className="text-gray-600">계정이 없으신가요? </span>
                  <Link to="/signup" className="text-primary font-semibold hover:underline">
                    회원가입
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 모바일용 서비스 소개 */}
          <div className="mt-8 lg:hidden w-full max-w-md pb-8">
            <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-6 text-white">
              <div className="text-center mb-5">
                <h2 className="text-lg font-bold">ALT Generation</h2>
                <p className="text-blue-100 text-sm mt-1">AI 기반 이미지 ALT 텍스트 자동 생성 시스템</p>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">빠른 생성</p>
                    <p className="text-blue-100 text-xs mt-0.5">AI 기술을 이용한 초고속 ALT 텍스트 생성</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <Lock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">안전한 관리</p>
                    <p className="text-blue-100 text-xs mt-0.5">엔터프라이즈급 보안으로 데이터 보호</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-white/20">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">상세 분석</p>
                    <p className="text-blue-100 text-xs mt-0.5">생성된 ALT 텍스트의 품질 및 사용 현황 분석</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/20">
                <p className="text-blue-100 text-center text-xs">
                  웹사이트 크롤링, 이미지 URL, 이미지 업로드 등<br />다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 비밀번호 재설정 페이지 (이메일 링크 클릭 시)
export const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // URL에서 토큰 추출
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('유효하지 않은 링크입니다.');
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!PASSWORD_REGEX.test(password)) {
      setError(PASSWORD_RULE_MESSAGE);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        newPassword: password,
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || '비밀번호 재설정에 실패했습니다.');
      }
    } catch (err: any) {
      const data = err.response?.data;
      const fieldMessage = data?.errors?.[0]?.message;
      setError(fieldMessage || data?.message || '비밀번호 재설정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* 좌측: 서비스 소개 */}
          <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

            <div className="relative z-10 max-w-md">
              <div className="mb-8 flex items-center justify-center">
                <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
                </div>
              </div>

              <Link to="/" className="block">
                <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                  ALT Generation
                </h1>
              </Link>
              <p className="text-xl text-blue-100 text-center mb-12">
                AI 기반 이미지 ALT 텍스트 자동 생성 시스템
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <Zap className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                    <p className="text-blue-100">
                      AI 기술을 이용한 초고속 ALT 텍스트 생성
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <Lock className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                    <p className="text-blue-100">
                      엔터프라이즈급 보안으로 데이터 보호
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <BarChart3 className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                    <p className="text-blue-100">
                      생성된 ALT 텍스트의 품질 및 사용 현황 분석
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white border-opacity-20">
                <p className="text-blue-100 text-center text-sm">
                  웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
                </p>
              </div>
            </div>
          </div>

          {/* 우측: 에러 메시지 */}
          <div className="flex flex-col justify-center items-center p-6 md:p-12">
            <Card className="w-full max-w-md shadow-lg">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4 lg:hidden">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-lg">
                    <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-12 h-12 rounded-full" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-red-600">유효하지 않은 링크</CardTitle>
              </CardHeader>
              <CardContent>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    비밀번호 재설정 링크가 유효하지 않거나 만료되었습니다.
                  </AlertDescription>
                </Alert>
                <Link to="/find-account" className="block mt-4">
                  <Button className="w-full">다시 요청하기</Button>
                </Link>
                <div className="mt-6 text-center">
                  <Link to="/login" className="text-sm text-primary hover:underline">
                    로그인으로 돌아가기
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* 좌측: 서비스 소개 */}
          <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

            <div className="relative z-10 max-w-md">
              <div className="mb-8 flex items-center justify-center">
                <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
                </div>
              </div>

              <Link to="/" className="block">
                <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                  ALT Generation
                </h1>
              </Link>
              <p className="text-xl text-blue-100 text-center mb-12">
                AI 기반 이미지 ALT 텍스트 자동 생성 시스템
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <Zap className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                    <p className="text-blue-100">
                      AI 기술을 이용한 초고속 ALT 텍스트 생성
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <Lock className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                    <p className="text-blue-100">
                      엔터프라이즈급 보안으로 데이터 보호
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                      <BarChart3 className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                    <p className="text-blue-100">
                      생성된 ALT 텍스트의 품질 및 사용 현황 분석
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white border-opacity-20">
                <p className="text-blue-100 text-center text-sm">
                  웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
                </p>
              </div>
            </div>
          </div>

          {/* 우측: 성공 메시지 */}
          <div className="flex flex-col justify-center items-center p-6 md:p-12">
            <Card className="w-full max-w-md shadow-lg">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 p-4 rounded-full">
                    <CheckCircle2 className="w-16 h-16 text-green-600" />
                  </div>
                </div>
                <CardTitle className="text-3xl font-bold text-green-600">비밀번호 변경 완료</CardTitle>
                <CardDescription className="text-base mt-2">
                  비밀번호가 성공적으로 변경되었습니다.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/login">
                  <Button className="w-full" size="lg">로그인하러 가기</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* 좌측: 서비스 소개 */}
        <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-10 rounded-full -ml-48 -mb-48"></div>

          <div className="relative z-10 max-w-md">
            <div className="mb-8 flex items-center justify-center">
              <div className="bg-white bg-opacity-40 p-4 rounded-full backdrop-blur-sm shadow-lg">
                <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-16 h-16 rounded-full" />
              </div>
            </div>

            <Link to="/" className="block">
              <h1 className="text-5xl font-bold text-center mb-4 hover:text-blue-200 transition-colors cursor-pointer">
                ALT Generation
              </h1>
            </Link>
            <p className="text-xl text-blue-100 text-center mb-12">
              AI 기반 이미지 ALT 텍스트 자동 생성 시스템
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">빠른 생성</h3>
                  <p className="text-blue-100">
                    AI 기술을 이용한 초고속 ALT 텍스트 생성
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">안전한 관리</h3>
                  <p className="text-blue-100">
                    엔터프라이즈급 보안으로 데이터 보호
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-white bg-opacity-20">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">상세 분석</h3>
                  <p className="text-blue-100">
                    생성된 ALT 텍스트의 품질 및 사용 현황 분석
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white border-opacity-20">
              <p className="text-blue-100 text-center text-sm">
                웹사이트 크롤링, 이미지 URL, 이미지 업로드 등 <br /> 다양한 방식으로 ALT 텍스트를 생성할 수 있습니다
              </p>
            </div>
          </div>
        </div>

        {/* 우측: 비밀번호 재설정 폼 */}
        <div className="flex flex-col justify-center items-center p-6 md:p-12">
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4 lg:hidden">
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-12 h-12 rounded-full" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold">새 비밀번호 설정</CardTitle>
              <CardDescription className="text-base mt-2">
                새로운 비밀번호를 입력해주세요
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    새 비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="8자 이상, 영문/숫자/특수문자 포함"
                      required
                      disabled={loading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* 비밀번호 조건 실시간 체크 */}
                  <ul className="mt-2 space-y-1">
                    {[
                      { label: '10자 이상', ok: password.length >= 10 },
                      { label: '대문자 포함', ok: /[A-Z]/.test(password) },
                      { label: '소문자 포함', ok: /[a-z]/.test(password) },
                      { label: '숫자 포함', ok: /\d/.test(password) },
                      { label: '특수문자 포함 (@$!%*#?&)', ok: /[@$!%*#?&]/.test(password) },
                    ].map(({ label, ok }) => (
                      <li
                        key={label}
                        className={`text-xs flex items-center gap-1 ${
                          password.length === 0
                            ? 'text-gray-400'
                            : ok
                            ? 'text-green-600'
                            : 'text-red-500'
                        }`}
                      >
                        {password.length === 0 ? '•' : ok ? '✓' : '✗'} {label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    새 비밀번호 확인 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="비밀번호를 다시 입력하세요"
                      required
                      disabled={loading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && (
                    <p
                      className={`text-sm mt-1 ${
                        password === confirmPassword ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {password === confirmPassword
                        ? '✓ 비밀번호가 일치합니다'
                        : '✗ 비밀번호가 일치하지 않습니다'}
                    </p>
                  )}
                </div>

                <Button type="submit" disabled={loading} className="w-full" size="lg">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      변경 중...
                    </>
                  ) : (
                    '비밀번호 변경'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-primary hover:underline">
                  로그인으로 돌아가기
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
